'use client';

import clsx from 'clsx';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';

import { useCarQualityAnimationScroll } from '../carQualityAnimation.helpers';

type Props = {
  snapPoints: [number, number][];
};

type ProgressSequenceProps = {
  from: number;
  to: number;
};

export const ScrollProgress: FC<Props> = ({ snapPoints }) => (
  <div
    className="fixed top-[50lvh] right-2 isolate flex -translate-y-1/2 flex-col items-center justify-center sm:right-4"
    data-testid="scroll-progress"
  >
    {snapPoints.map(([from, to], index) => (
      <Fragment key={`${from}-${to}`}>
        {index > 0 && <ScrollProgressTrack from={snapPoints[index - 1][1]} to={from} />}
        <ScrollProgressDot key={`${from}-${to}`} from={from} to={to} />
      </Fragment>
    ))}
  </div>
);

const ScrollProgressDot: FC<ProgressSequenceProps> = ({ from, to }) => {
  const [activeState, setActiveState] = useState<'active' | 'inactive' | 'current'>('inactive');
  const { scrollYProgress } = useCarQualityAnimationScroll();
  const activeStateCallback = useCallback(
    (scroll: number) => {
      if (scroll >= from && scroll <= to) {
        setActiveState('current');
      } else if (scroll > to) {
        setActiveState('active');
      } else {
        setActiveState('inactive');
      }
    },
    [from, to],
  );

  scrollYProgress.on('change', activeStateCallback);

  useEffect(() => {
    activeStateCallback(scrollYProgress.get());
  }, [scrollYProgress]);

  return (
    <div
      className={clsx(
        'relative z-10 h-2 w-2 rounded-full border transition-all duration-150 sm:h-3 sm:w-3',
        activeState === 'active' && 'border-for-pri-acc bg-for-pri-acc',
        activeState === 'current' && 'border-for-pri-acc bg-white ring-1 ring-for-pri-acc sm:ring-2',
        activeState === 'inactive' && 'border-for-neu-tie3 bg-for-neu-tie3',
      )}
      data-testid="scroll-progress-dot"
    />
  );
};

const ScrollProgressTrack: FC<ProgressSequenceProps> = ({ from, to }) => {
  const [progress, setProgress] = useState(0);
  const { scrollYProgress } = useCarQualityAnimationScroll();

  const progressCallback = useCallback(
    (scroll: number) => {
      if (scroll >= from && scroll < to) {
        setProgress((scroll - from) / (to - from));
      } else if (scroll >= to) {
        setProgress(1);
      } else {
        setProgress(0);
      }
    },
    [from, to],
  );

  scrollYProgress.on('change', progressCallback);
  useEffect(() => {
    progressCallback(scrollYProgress.get());
  }, [scrollYProgress]);

  return (
    <div className="relative h-8 w-0.5 bg-for-neu-tie3 sm:h-10 sm:w-1" data-testid="scroll-progress-track">
      <div
        className="absolute top-0 left-0 h-full w-full origin-top bg-for-pri-acc"
        style={{ transform: `scaleY(${progress})` }}
      />
    </div>
  );
};
